import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  IAbsenceRequestDTO,
  IAbsenceRequestParams,
  IAbsenceRequestResultDTO,
  IAbsenceStatusDTO,
  IAbsenceType,
  IApproverDTO,
  ICreateAbsenceRequestDTO,
  IScheduledAbsenceDTO,
} from 'src/api/dtos';
import AbsenceTypeRepository from 'src/api/repositories/common/v1/absenceType.repository';
import AbsenceRepository from 'src/api/repositories/ess/v1/absence.repository';
import api from 'src/api/rtk/rtk-api';
import { SortDirectionEnums, SystemTimeCategoryGroupDescriptionEnums } from 'src/shared/enums';

import { resetNewAbsenceActionCreator } from './absences.slice';
import { AbsenceDispatch } from './absences.types';

export const fetchAbsences = createAsyncThunk<IAbsenceRequestDTO[], IAbsenceRequestParams | undefined>(
  'absence/absences',
  async (params: IAbsenceRequestParams | undefined, { rejectWithValue }) => {
    try {
      const repository = new AbsenceRepository();
      return await repository.get(params);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const createAbsence = createAsyncThunk<IAbsenceRequestResultDTO, ICreateAbsenceRequestDTO>(
  'absence/create-absence',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const repository = new AbsenceRepository();
      const response = await repository.create(payload);
      dispatch(api.util.invalidateTags(['Calendar']));
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const cancelAbsence = createAsyncThunk<
  IAbsenceRequestDTO[],
  { notificationIds: number[]; params: IAbsenceRequestParams }
>('absence/update-absence', async ({ notificationIds, params }, { dispatch, rejectWithValue }) => {
  try {
    const repository = new AbsenceRepository();
    const response = await repository.cancelAbsence(notificationIds);

    dispatch(api.util.invalidateTags(['Calendar']));

    if (Object.values(params).length > 0) {
      dispatch(fetchAbsences(params));
    }

    return response;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const fetchScheduledAbsences = createAsyncThunk<IScheduledAbsenceDTO[], { sortDirection?: SortDirectionEnums }>(
  'absence/scheduledAbsences',
  async (params, { rejectWithValue }) => {
    try {
      const repository = new AbsenceRepository();
      return await repository.getScheduledAbsences(params);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchApprovers = createAsyncThunk<IApproverDTO[], void>(
  'absence/approvers',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new AbsenceRepository();
      return await repository.getApprovers();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchAbsenceStatuses = createAsyncThunk<IAbsenceStatusDTO[], void>(
  'absence/statuses',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new AbsenceRepository();
      return await repository.getStatuses();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchAbsenceTypes = createAsyncThunk<IAbsenceType[], void>(
  'absence/types',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new AbsenceTypeRepository();

      const [listPartialTrue, listPartialFalse] = await Promise.all([
        repository.get({ timeCategoryGroupDescription: SystemTimeCategoryGroupDescriptionEnums.PARTIAL_ABSENCES }),
        repository.get({ timeCategoryGroupDescription: SystemTimeCategoryGroupDescriptionEnums.ABSENCE_REQUEST_TCG }),
      ]);

      const list1: IAbsenceType[] = listPartialTrue.map((type) => ({ ...type, isPartialDay: true }));
      const list2: IAbsenceType[] = listPartialFalse.map((type) => ({ ...type, isPartialDay: false }));

      return [...list1, ...list2];
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const resetNewAbsenceItem = () => {
  return (dispatch: AbsenceDispatch): void => {
    dispatch(resetNewAbsenceActionCreator());
  };
};
